/**
 * Execution Plan Card
 *
 * Renders a list of nodes that will be executed with per-node spark costs,
 * plus "Let's go!" / "Cancel" buttons. Used for cost confirmation questions.
 */

import { memo, useState, useEffect } from 'react'
import { cn } from '../../lib/utils'
import { nodeSchemas } from '../canvas-v2/nodeRegistry'

interface NodeDetail {
  node_id: string
  type: string // backend snake_case type
  label: string
  sparks: number
  batch_child?: boolean // true for panel nodes auto-created by storyboard tool
}

export interface ExecutionPlanCardProps {
  nodes: NodeDetail[]
  totalSparks: number
  selectedOption?: string
  onSelect: (option: string) => void
  onNodeClick?: (nodeId: string) => void
  disabled?: boolean
}

// Build a reverse lookup: backend_type → { icon, label } from nodeSchemas
const backendTypeInfo: Record<string, { icon: string; label: string }> = {}
for (const [, schema] of Object.entries(nodeSchemas)) {
  const s = schema as { backendType?: string; icon?: string; label?: string }
  if (s.backendType) {
    backendTypeInfo[s.backendType] = { icon: s.icon ?? '', label: s.label ?? s.backendType }
  }
}

/**
 * Collapse batch_child nodes (e.g. storyboard panels with in_update=True)
 * into their parent row. Shows "+ N panels" on the parent instead.
 */
function collapseNodes(nodes: NodeDetail[]): { visible: NodeDetail[]; childCountByParent: Map<string, number> } {
  const batchChildren = nodes.filter((n) => n.batch_child)
  const visible = nodes.filter((n) => !n.batch_child)

  const childCountByParent = new Map<string, number>()
  if (batchChildren.length > 0) {
    // Match children to their parent by shared timestamp suffix
    for (const child of batchChildren) {
      const tsMatch = child.node_id.match(/_(\d+)$/)
      if (!tsMatch) continue
      const ts = tsMatch[1]
      const parent = visible.find((n) => n.node_id.endsWith(ts))
      if (parent) {
        childCountByParent.set(parent.node_id, (childCountByParent.get(parent.node_id) ?? 0) + 1)
      }
    }
  }
  return { visible, childCountByParent }
}

export const ExecutionPlanCard = memo(function ExecutionPlanCard({
  nodes,
  totalSparks,
  selectedOption,
  onSelect,
  onNodeClick,
  disabled = false,
}: ExecutionPlanCardProps) {
  const [localSelected, setLocalSelected] = useState<string | undefined>(selectedOption)
  const hasSelection = !!localSelected || !!selectedOption

  useEffect(() => {
    setLocalSelected(selectedOption)
  }, [selectedOption])

  const handleSelect = (value: string) => {
    if (disabled || hasSelection) return
    setLocalSelected(value)
    onSelect(value)
  }

  const sparkWord = totalSparks === 1 ? 'spark' : 'sparks'
  const { visible, childCountByParent } = collapseNodes(nodes)

  return (
    <div className="my-4 animate-fade-in-down">
      {/* Node list */}
      <div className="rounded-lg border border-border-subtle overflow-hidden">
        <div className="px-3 py-2 bg-surface-1 border-b border-border-subtle">
          <span className="text-xs font-medium text-text-muted uppercase tracking-wider">
            Execution Plan
          </span>
        </div>
        <div className="divide-y divide-border-subtle">
          {visible.map((node) => {
            const info = backendTypeInfo[node.type]
            const icon = info?.icon ?? ''
            const typeLabel = info?.label ?? node.type
            const childCount = childCountByParent.get(node.node_id)
            return (
              <div
                key={node.node_id}
                className={cn(
                  'px-3 py-2 flex items-center gap-2',
                  onNodeClick && 'cursor-pointer hover:bg-surface-2 transition-colors',
                )}
                onClick={() => onNodeClick?.(node.node_id)}
              >
                {icon && <span className="text-sm flex-shrink-0">{icon}</span>}
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-text-primary truncate">
                    {node.label}
                    {childCount != null && (
                      <span className="text-text-muted font-normal"> + {childCount} panels</span>
                    )}
                  </div>
                  <div className="text-xs text-text-muted">{typeLabel}</div>
                </div>
                <div className="text-xs text-text-muted flex-shrink-0">
                  {node.sparks} {node.sparks === 1 ? 'spark' : 'sparks'}
                </div>
              </div>
            )
          })}
        </div>
        <div className="px-3 py-2 bg-surface-1 border-t border-border-subtle flex justify-between items-center">
          <span className="text-sm font-medium text-text-primary">Total</span>
          <span className="text-sm font-medium text-text-primary">
            {totalSparks} {sparkWord}
          </span>
        </div>
      </div>

      {/* Action buttons */}
      {!hasSelection && (
        <div className="flex gap-2 mt-3 animate-fade-in-down" style={{ animationDelay: '200ms' }}>
          <button
            onClick={() => handleSelect('confirm')}
            disabled={disabled}
            className={cn(
              'flex-1 px-3 py-2 text-sm font-medium rounded border transition-colors',
              'bg-primary text-primary-foreground border-primary',
              'hover:bg-primary/90',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
          >
            Let&apos;s go!
          </button>
          <button
            onClick={() => handleSelect('cancel')}
            disabled={disabled}
            className={cn(
              'px-3 py-2 text-sm font-medium rounded border transition-colors',
              'border-border-subtle text-text-muted',
              'hover:border-border hover:text-text-secondary',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
          >
            Cancel
          </button>
        </div>
      )}

      {/* Selection feedback */}
      {hasSelection && (
        <div className="mt-2 text-sm text-text-muted animate-fade-in-down">
          {(localSelected || selectedOption) === 'confirm'
            ? '✨ Approved'
            : 'Cancelled'}
        </div>
      )}
    </div>
  )
})
